import cx from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MARKETING_LANGUAGES,
  MARKETING_PHONE,
  handoffLanguage,
  marketingFooterCopy,
  marketingHeaderChrome,
  marketingHeaderLinks,
  marketingHomeHref,
  marketingMobileLinks,
} from '../util/marketingSite';
import useLanguageSwitch from '../i18n/useLanguageSwitch';
import {
  ROUTE_ACCOUNT_ROOT,
  ROUTE_AUTH_LOGIN,
  ROUTE_BOOK_LOCATION,
  ROUTE_FLEET_VEHICLES,
} from '../util/constants';
import { Franchise } from '../interfaces/FranchiseNavBar';
import { useMarketingLinkProps } from '../util/stepNavigation';

/**
 * The marketing site's header, reproduced for the booking portal.
 *
 * This is a transcription of the site's SSR `nav()` in
 * `aperio/site/build/shell.js`, not an interpretation of it: same elements in
 * the same order (logo, centre links, language pill, Log in, navy CTA,
 * burger, mobile panel), and the CSS in `public/aperio/theme.css` is the
 * site's own nav rules from `styles.css` / `pages.css` / `responsive.css`,
 * namespaced `apr-` and scoped to the preset. The point is that the bar does
 * not change across the hand-off, so anything that drifts from the site here
 * is a bug.
 *
 * The mobile panel BEHAVES as the site's does (site.js "mobile menu", QA
 * 2026-09-25 #47): a modal dialog around the panel, focus into it on open
 * and trapped there, the rest of the page inert and not scrollable, Escape
 * or the panel's "Close menu" link closes it and puts focus back on the
 * burger, a link closes it, and it lets go when the burger is gone (resize
 * to desktop). The burger draws an X while open (theme.css). The panel's
 * rows are the site's: language first, then its links, Contact included.
 *
 * Three things are the portal's rather than the site's, and only these three:
 * - the navy CTA is a router Link into the booking flow, not an href back out;
 * - "Log in" follows the portal's own rule - it is the account link once you
 *   are signed in, and the mobile panel gains "Log out";
 * - franchise staff keep their franchise selector, in the site's own dropdown
 *   button style.
 *
 * Only ever rendered for a tenant on a non-default theme preset, so no other
 * tenant's header is touched.
 */

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),'
  + 'select:not([disabled]),textarea:not([disabled]),'
  + '[tabindex]:not([tabindex="-1"])';

/** The panel's own tab stops, in order (hidden rows excluded). */
function focusables(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE))
    .filter((el) => !el.closest('[hidden]'));
}

type Props = {
  /** The marketing site root, already normalized (no trailing slash). */
  marketingUrl: string;
  isLoggedIn: boolean;
  /** First name on the signed-in account button (the site's, site.js). */
  accountName?: string;
  onLogOut: () => void;
  /** A signed-in fleet account books from its dashboard, not the plans. */
  fleetAccount?: boolean;
  /** Franchise selector, shown only for staff roles. */
  franchises: Franchise[];
  franchiseName?: string;
  onSelectFranchise: (franchise: Franchise) => void;
};

export default function AperioSiteHeader({
  marketingUrl,
  isLoggedIn,
  accountName = '',
  fleetAccount = false,
  onLogOut,
  franchises,
  franchiseName,
  onSelectFranchise,
}: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [franchiseOpen, setFranchiseOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const acctRef = useRef<HTMLDivElement>(null);
  const acctBtnRef = useRef<HTMLButtonElement>(null);
  const burgerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const franchiseBtnRef = useRef<HTMLButtonElement>(null);
  // Re-renders the bar when the portal's language changes.
  useTranslation();
  const switcher = useLanguageSwitch();
  const onFleetDashboard = useLocation().pathname.startsWith('/fleet');
  // A fleet login's "Book" opens its Vehicles tab (pick vans, one-off fleet
  // service), never the consumer plans (John's test, 2026-10-28).
  const bookRoute = fleetAccount ? ROUTE_FLEET_VEHICLES : ROUTE_BOOK_LOCATION;

  // The pill shows which language the visitor was reading the site in, so the
  // active segment carries over the hand-off instead of always claiming EN.
  const activeLang = handoffLanguage();
  const headerLinks = marketingHeaderLinks(marketingUrl, activeLang);
  const mobileLinks = marketingMobileLinks(marketingUrl, activeLang);
  const chrome = marketingHeaderChrome(activeLang);
  // Mid-booking, links out open in a new tab (util/stepNavigation).
  const outLink = useMarketingLinkProps();

  const closeAll = () => {
    setMenuOpen(false);
    setFranchiseOpen(false);
    setAcctOpen(false);
  };

  // The account menu closes on a click or focus outside it, and on Escape
  // (focus back on its button), like the site's.
  useEffect(() => {
    if (!acctOpen) return undefined;
    const outside = (event: Event) => {
      if (!acctRef.current?.contains(event.target as Node)) setAcctOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setAcctOpen(false);
      acctBtnRef.current?.focus();
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('focusin', outside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('focusin', outside);
      document.removeEventListener('keydown', onKey);
    };
  }, [acctOpen]);

  /** Close the panel and give focus back to the control that opened it. */
  const closeMenuToBurger = () => {
    setMenuOpen(false);
    burgerRef.current?.focus();
  };

  // While the panel is open: the page behind is inert and does not scroll,
  // and focus starts on the panel's first row. All of it is undone on close,
  // on unmount, and if the burger disappears (rotated / resized to desktop),
  // which would otherwise leave a locked page with no visible way out.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const header = headerRef.current;
    const doc = document;
    const inerted: Element[] = [];
    const keep = (el: Element) => !!header && (el === header
      || el.contains(header));
    const inertOutside = (parent: Element | null) => {
      if (!parent) return;
      Array.from(parent.children).forEach((el) => {
        if (keep(el) || el.hasAttribute('inert')) return;
        el.setAttribute('inert', '');
        inerted.push(el);
      });
    };
    // The header's siblings (skip link, <main>, footer) and everything else
    // on <body> (the app root's siblings, pop-ups).
    inertOutside(header?.parentElement || null);
    inertOutside(doc.body);
    doc.documentElement.classList.add('apr-menu-lock');
    doc.body.classList.add('apr-menu-lock');
    const first = focusables(panelRef.current)[0];
    first?.focus();

    const burgerGone = () => {
      const burger = burgerRef.current;
      if (!burger) return true;
      const view = doc.defaultView;
      return !!view && view.getComputedStyle(burger).display === 'none';
    };
    const onResize = () => {
      if (burgerGone()) setMenuOpen(false);
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      inerted.forEach((el) => el.removeAttribute('inert'));
      doc.documentElement.classList.remove('apr-menu-lock');
      doc.body.classList.remove('apr-menu-lock');
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [menuOpen]);

  // Escape closes whatever is open, from anywhere on the page; Tab and
  // Shift+Tab cycle inside the open panel (the site's trap: the panel's own
  // rows, not the burger).
  useEffect(() => {
    if (!menuOpen && !franchiseOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (menuOpen) {
          closeMenuToBurger();
        } else {
          setFranchiseOpen(false);
          franchiseBtnRef.current?.focus();
        }
        return;
      }
      if (event.key !== 'Tab' || !menuOpen) return;
      const items = focusables(panelRef.current);
      if (!items.length) return;
      const index = items.indexOf(document.activeElement as HTMLElement);
      let next: HTMLElement;
      if (event.shiftKey) {
        next = items[(index <= 0 ? items.length : index) - 1];
      } else {
        next = items[index < 0 || index === items.length - 1 ? 0 : index + 1];
      }
      event.preventDefault();
      next.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  // closeMenuToBurger only touches refs and a setter.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuOpen, franchiseOpen]);

  const accountLabel = isLoggedIn ? chrome.account : chrome.login;
  const accountRoute = isLoggedIn ? ROUTE_ACCOUNT_ROOT : ROUTE_AUTH_LOGIN;

  // The portal speaks the site's three languages (Aperio preset): each
  // option switches this page in place and keeps the booking. Without the
  // portal's translations it links to the site's home in that language, as
  // it always did.
  const languagePill = (inPanel = false) => (
    <div
      className="apr-seg apr-seg--lang"
      role="group"
      aria-label={chrome.lang}
      // The site's own inline style on the panel's copy (shell.js nav()).
      style={inPanel
        ? { margin: '2px 0 10px', alignSelf: 'flex-start' }
        : undefined}
    >
      {MARKETING_LANGUAGES.map((lang) => (
        <a
          key={lang.code}
          className={cx({ 'apr-seg__on': lang.code === activeLang })}
          aria-current={lang.code === activeLang ? 'true' : undefined}
          href={switcher.active
            ? switcher.hrefFor(lang.code)
            : `${marketingUrl}${lang.path}`}
          hrefLang={lang.hrefLang}
          target={switcher.active ? undefined : outLink.target}
          rel={switcher.active ? undefined : outLink.rel}
          lang={lang.code}
          aria-label={lang.name}
          onClick={(event) => {
            closeAll();
            if (switcher.active) switcher.switchTo(lang.code, event);
          }}
        >
          {lang.label}
        </a>
      ))}
    </div>
  );

  return (
    <header className="apr-nav" ref={headerRef}>
      <div className="apr-nav__wrap">
        {/* Same tab: this is the visitor going back to the site they came
            from, which is what the site's own logo link does too. */}
        {/* The site's logo is this wordmark - a navy mark and the name - not
            an image, so it is what the portal shows too. The tenant's
            uploaded logo (tenant.logoUrl) is the white-on-transparent one
            drawn for the default preset's dark bar; on this paper bar it is
            invisible (audit), so it is deliberately not used here. */}
        <a
          className="apr-logo notranslate"
          translate="no"
          href={marketingHomeHref(marketingUrl, activeLang)}
          onClick={closeAll}
        >
          <span className="apr-logo-mark" aria-hidden="true">A</span>
          Aperio
          {/* On the fleet dashboard the product is "Aperio Fleet". */}
          {onFleetDashboard && ' Fleet'}
        </a>

        <nav className="apr-nav-links" aria-label={chrome.mainNav}>
          {headerLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              target={outLink.target}
              rel={outLink.rel}
              onClick={closeAll}
            >
              {link.label}
            </a>
          ))}
          {franchises.length > 0 && (
            <span className="apr-nav-dd">
              <button
                type="button"
                className="apr-nav-dd__btn"
                ref={franchiseBtnRef}
                aria-haspopup="true"
                aria-expanded={franchiseOpen}
                onClick={() => setFranchiseOpen((prev) => !prev)}
              >
                {franchiseName || 'Select a Franchise'}
              </button>
              {franchiseOpen && (
                <span className="apr-nav-menu">
                  {franchises.map((franchise) => (
                    <button
                      key={franchise.id}
                      type="button"
                      onClick={() => {
                        onSelectFranchise(franchise);
                        closeAll();
                      }}
                    >
                      {franchise.name}
                    </button>
                  ))}
                </span>
              )}
            </span>
          )}
        </nav>

        <div className="apr-nav-right">
          {languagePill(false)}
          {isLoggedIn ? (
            // The site's signed-in account button (site.js "signed in"):
            // initial + first name, opening My account / Log out.
            <div className="apr-acct" ref={acctRef}>
              <button
                type="button"
                className="apr-acct__btn"
                ref={acctBtnRef}
                aria-haspopup="true"
                aria-expanded={acctOpen}
                aria-controls="apr-acct-menu"
                aria-label={accountName
                  ? `${chrome.acctMenu}: ${accountName}` : chrome.acctMenu}
                onClick={() => {
                  setAcctOpen((prev) => !prev);
                  setFranchiseOpen(false);
                }}
              >
                <span className="apr-acct__ava" aria-hidden="true" data-mp-mask>
                  {(accountName.charAt(0) || '\u2022').toUpperCase()}
                </span>
                {accountName && (
                  <span className="apr-acct__name" data-mp-mask>{accountName}</span>
                )}
                <span className="apr-acct__caret" aria-hidden="true" />
              </button>
              {acctOpen && (
                <div className="apr-acct__menu" id="apr-acct-menu">
                  {accountName && (
                    <p className="apr-acct__who">
                      {chrome.signedIn.split('{name}')[0]}
                      <b data-mp-mask>{accountName}</b>
                      {chrome.signedIn.split('{name}')[1] || ''}
                    </p>
                  )}
                  <Link to={accountRoute} onClick={closeAll}>{accountLabel}</Link>
                  <Link
                    className="apr-acct__out"
                    to={ROUTE_AUTH_LOGIN}
                    onClick={() => {
                      closeAll();
                      onLogOut();
                    }}
                  >
                    {chrome.logout}
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <Link className="apr-login" to={accountRoute} onClick={closeAll}>
              {accountLabel}
            </Link>
          )}
          {/* A plain text link, as on the site (round 2, 2026-10-25). */}
          <Link
            className="apr-book"
            to={bookRoute}
            onClick={closeAll}
          >
            {chrome.book}
          </Link>
          <button
            type="button"
            className="apr-burger"
            ref={burgerRef}
            aria-label={menuOpen ? chrome.closeMenu : chrome.menu}
            aria-expanded={menuOpen}
            aria-controls="apr-nav-mobile"
            onClick={() => {
              setMenuOpen((prev) => !prev);
              setFranchiseOpen(false);
            }}
          >
            <span />
            <span />
          </button>
        </div>
      </div>

      {/* The site wraps its panel in a modal dialog the same way (site.js:
          the dialog is a wrapper so the <nav> keeps its landmark role). */}
      <div
        className="apr-nav-dialog"
        role={menuOpen ? 'dialog' : undefined}
        aria-modal={menuOpen ? true : undefined}
        aria-label={menuOpen ? chrome.menu : undefined}
      >
        <nav
          className={cx('apr-nav-mobile', { 'apr-nav-mobile--open': menuOpen })}
          id="apr-nav-mobile"
          aria-label={chrome.mobileNav}
          ref={panelRef}
        >
          {/* First row, as on the site since its visual audit: the language
              switch sat ten rows down, below the fold of a phone. */}
          {languagePill(true)}
          {/* Staff only: the franchise switch comes right after the language,
              labelled, so a tech on a phone does not have to scroll past the
              site's links to find it (owner, 2026-10-28). */}
          {franchises.length > 0 && (
            <p className="apr-nav-mobile__label">
              {franchiseName ? `Franchise: ${franchiseName}` : 'Select a Franchise'}
            </p>
          )}
          {franchises.map((franchise) => (
            <button
              key={franchise.id}
              type="button"
              className="apr-nav-mobile__franchise"
              aria-current={franchise.name === franchiseName ? 'true' : undefined}
              onClick={() => {
                onSelectFranchise(franchise);
                closeAll();
              }}
            >
              {franchise.name}
            </button>
          ))}
          {mobileLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              target={outLink.target}
              rel={outLink.rel}
              onClick={closeAll}
            >
              {link.label}
            </a>
          ))}
          <a href={`tel:${MARKETING_PHONE}`} onClick={closeAll}>
            {marketingFooterCopy(activeLang).phoneHuman}
          </a>
          <Link to={accountRoute} onClick={closeAll}>{accountLabel}</Link>
          {isLoggedIn && (
            <Link
              to={ROUTE_AUTH_LOGIN}
              onClick={() => {
                closeAll();
                onLogOut();
              }}
            >
              {chrome.logout}
            </Link>
          )}
          <Link to={bookRoute} onClick={closeAll}>{chrome.book}</Link>
          {/* The site's .nav-close: with script it closes like Escape and
              returns focus to the burger. */}
          <a
            className="apr-nav-close"
            href="#main"
            onClick={(event) => {
              event.preventDefault();
              closeMenuToBurger();
            }}
          >
            {chrome.closeMenu}
          </a>
        </nav>
      </div>
    </header>
  );
}
